import type {
    MRT_ColumnOrderState,
    MRT_ColumnSizingState,
    MRT_SortingState,
    MRT_VisibilityState,
    MRT_RowData,
    MRT_TableOptions,
    MRT_Row,
    MRT_ColumnPinningState,
    MRT_TableInstance,
} from 'material-react-table'
import { useMaterialReactTable } from 'material-react-table'
import { ContentCopy } from '@mui/icons-material'
import { type Updater, type FilterFn } from '@tanstack/table-core'
import { defaultRangeExtractor, type Range } from '@tanstack/react-virtual'
import { useKey, useLocalStorage } from 'react-use'
import { type Dispatch, type SetStateAction, useMemo, useRef } from 'react'
import { alertClasses, buttonClasses, selectClasses, } from '@mui/material'
import _ from 'lodash'

declare module '@tanstack/table-core' {
    interface FilterFns {
        objectId: FilterFn<MRT_RowData>
    }
}

export interface TableOptions<TData extends MRT_RowData>
    extends MRT_TableOptions<TData> {
    isPrimaryBackgroundColor?: boolean
}

export const useAdminMaterialReactTable = <TData extends MRT_RowData>(
    options: TableOptions<TData>,
    tableName: string,
) => {
    const [localColumnVisibility, setLocalColumnVisibility] =
        useLocalStorage<MRT_VisibilityState>(
            `${tableName}-column-visibility`,
            options.initialState?.columnVisibility ?? {},
        )
    // "Show all" and "Hide all" toggle every column in the same tick, so
    // each update has to build on the previous one rather than on the
    // state captured at render.
    const latestColumnVisibility = useRef(localColumnVisibility)
    latestColumnVisibility.current = localColumnVisibility
    const [localColumnSizing, setLocalColumnSizing] =
        useLocalStorage<MRT_ColumnSizingState>(
            `${tableName}-column-sizing`,
            options.initialState?.columnSizing ?? {},
        )
    const [localColumnOrder, setLocalColumnOrder] =
        useLocalStorage<MRT_ColumnOrderState>(
            `${tableName}-column-order`,
            getCleanColumnOrder(options.initialState?.columnOrder),
        )
    const [localColumnPinning, setLocalColumnPinning] =
        useLocalStorage<MRT_ColumnPinningState>(
            `${tableName}-column-pinning`,
            getCleanColumnPinning(options.initialState?.columnPinning),
        )
    const [localColumnSorting, setLocalColumnSorting] =
        useLocalStorage<MRT_SortingState>(
            `${tableName}-column-sorting`,
            options.initialState?.sorting ?? [],
        )
    useKey(
        (event) => (event.ctrlKey || event.metaKey) && event.key === 'f',
        (event) => {
            if (!table.getState().showColumnFilters) {
                event.preventDefault()
                table.setShowColumnFilters(true)
            }
        },
        { event: 'keydown' },
    )

    const setLatestColumnVisibility: Dispatch<SetStateAction<MRT_VisibilityState | undefined>> = (
        columnVisibility,
    ) => {
        const newColumnVisibility =
            columnVisibility instanceof Function
                ? columnVisibility(latestColumnVisibility.current)
                : columnVisibility
        latestColumnVisibility.current = newColumnVisibility
        setLocalColumnVisibility(newColumnVisibility)
    }

    const setLocalCleanColumnPinning: Dispatch<SetStateAction<MRT_ColumnPinningState | undefined>> = (
        columnPinning,
    ) => {
        const newColumnPinning =
            columnPinning instanceof Function
                ? columnPinning(localColumnPinning ?? {})
                : columnPinning
        const cleanColumnPinning = getCleanColumnPinning(newColumnPinning)
        setLocalColumnPinning(cleanColumnPinning)
    }

    const setLocalCleanColumnOrder: Dispatch<SetStateAction<MRT_ColumnOrderState | undefined>> = (
        columnOrder,
    ) => {
        const newColumnOrder =
            columnOrder instanceof Function
                ? columnOrder(localColumnOrder ?? [])
                : columnOrder
        const cleanColumnOrder = getCleanColumnOrder(newColumnOrder)
        setLocalColumnOrder(cleanColumnOrder)
    }

    const mergedOptions: MRT_TableOptions<MRT_RowData> = useMemo(() => {
        const {
            data,
            initialState,
            defaultColumn,
            defaultDisplayColumn,
            state,
            isPrimaryBackgroundColor,
            displayColumnDefOptions,
            onColumnVisibilityChange,
            onColumnSizingChange,
            onColumnOrderChange,
            onSortingChange,
            onColumnPinningChange,
            rowVirtualizerOptions,
            muiPaginationProps,
            ...rest
        } = options

        const getPaginationProps = (args: { table: MRT_TableInstance<MRT_RowData> }) => {
            const userProps =
                typeof muiPaginationProps === 'function'
                    ? muiPaginationProps(args as any)
                    : muiPaginationProps

            return {
                rowsPerPageOptions: [100, 500, 1000, 2000, 5000, 10000],
                boundaryCount: 1,
                siblingCount: 1,
                showFirstButton: false,
                showLastButton: false,
                ...userProps,
            }
        }

        const getRowVirtualizerOptions = (args: { table: MRT_TableInstance<MRT_RowData> }) => {
            const userOptions =
                typeof rowVirtualizerOptions === 'function'
                    ? rowVirtualizerOptions(args as any)
                    : rowVirtualizerOptions

            const getCount = () => {
                const rowCount = args.table.getRowModel().rows.length
                return args.table.options.renderDetailPanel
                    ? rowCount * 2
                    : rowCount
            }

            const baseRangeExtractor =
                userOptions?.rangeExtractor ?? defaultRangeExtractor

            return {
                ...userOptions,
                rangeExtractor: (range: Range) => {
                    const indexes = baseRangeExtractor(range)
                    const count = getCount()
                    return indexes.filter(
                        (index) => index >= 0 && index < count,
                    )
                },
            }
        }

        return {
            initialState: {
                pagination: {
                    pageSize: 1000,
                    pageIndex: 0,
                },
                density: 'compact',
                columnPinning: mergeColumnPinning(
                    defaultColumnPinning,
                    initialState?.columnPinning,
                ),
                ...initialState,
            },
            defaultColumn: {
                enableEditing: false,
                enableColumnFilter: false,
                enableSorting: false,
                enableGlobalFilter: false,
                enablePinning: true,
                ...defaultColumn,
            },
            defaultDisplayColumn: {
                visibleInShowHideMenu: false,
                enableClickToCopy: false,
                enableColumnActions: false,
                enableColumnDragging: false,
                enableColumnFilter: false,
                enableColumnOrdering: false,
                enableEditing: false,
                enableGlobalFilter: false,
                enableGrouping: false,
                enablePinning: false,
                enableHiding: false,
                enableResizing: false,
                enableSorting: false,
                ...defaultDisplayColumn,
            },
            data,
            displayColumnDefOptions: {
                ...displayColumnDefOptions,
            },
            enableEditing: true,
            enableFilters: true,
            enableColumnActions: false,
            enableColumnDragging: false,
            enableFilterMatchHighlighting: true,
            enableGlobalFilter: false,
            enableSorting: false,
            enableRowSelection: true,
            enableStickyHeader: true,
            enableStickyFooter: true,
            enableColumnOrdering: true,
            enableColumnPinning: true,
            enableDensityToggle: false,
            enableFacetedValues: false,
            enableColumnResizing: true,
            enableFullScreenToggle: false,
            memoMode: false,
            editDisplayMode: 'cell',
            paginationDisplayMode: 'pages',
            positionToolbarAlertBanner: 'bottom',
            muiEditTextFieldProps: {
                type: 'text',
                inputProps: { style: { fontSize: '0.875rem' } },
            },
            muiCopyButtonProps: {
                startIcon: <ContentCopy sx={{ width: '0.85em' }} />,
                sx: { justifyContent: 'flex-start', fontFamily: 'monospace' },
            },
            muiPaginationProps: getPaginationProps,
            muiTopToolbarProps: {
                sx: {
                    [`.${selectClasses.root}`]: { px: 1 },
                    [`.${alertClasses.root}`]: {
                        [`.${buttonClasses.root}`]: { p: 1, my: -1, },
                    },
                },
            },
            muiBottomToolbarProps: {
                sx: {
                    [`.${selectClasses.root}`]: { px: 1 },
                    [`.${alertClasses.root}`]: {
                        [`.${buttonClasses.root}`]: { p: 1, my: -1, },
                    },
                },
            },
            muiCircularProgressProps: {
                thickness: 5,
            },
            muiLinearProgressProps: {
                thickness: 2,
            },
            enableRowVirtualization: true,
            autoResetPageIndex: false,
            rowVirtualizerOptions: getRowVirtualizerOptions,
            muiTablePaperProps: { sx: { height: '100%' } },
            muiTableContainerProps: ({ table }) => ({
                sx: {
                    height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
                },
            }),
            columnResizeMode: 'onEnd',
            filterFns: {
                objectId: (
                    row: MRT_Row<TData>,
                    columnId: string,
                    filterValues: string[],
                ) => {
                    const getId = (object: unknown) => {
                        if (
                            object &&
                            typeof object === 'object' &&
                            'id' in object
                        ) {
                            return String(object.id)
                        }
                        return String(object)
                    }

                    const columnValue = row.getValue(columnId)
                    if (Array.isArray(columnValue)) {
                        const ids = columnValue.map(getId)
                        return _.intersection(ids, filterValues).length > 0
                    }
                    return filterValues.includes(getId(columnValue))
                },
            },
            state: {
                ...state,
                ...(localColumnVisibility && {
                    columnVisibility:
                        state?.columnVisibility ?? localColumnVisibility,
                }),
                ...(localColumnSizing && {
                    columnSizing: state?.columnSizing ?? localColumnSizing,
                }),
                ...(localColumnOrder && {
                    columnOrder: state?.columnOrder ?? localColumnOrder,
                }),
                ...(localColumnSorting && {
                    sorting: state?.sorting ?? localColumnSorting,
                }),
                ...(localColumnPinning && {
                    columnPinning: mergeColumnPinning(
                        defaultColumnPinning,
                        localColumnPinning,
                    ),
                }),
            },
            onColumnVisibilityChange: (updaterOrValue) =>
                persistLocalStorage(
                    updaterOrValue,
                    latestColumnVisibility.current ?? {},
                    setLatestColumnVisibility,
                    onColumnVisibilityChange,
                ),
            onColumnSizingChange: (updaterOrValue) =>
                persistLocalStorage(
                    updaterOrValue,
                    localColumnSizing ?? {},
                    setLocalColumnSizing,
                    onColumnSizingChange,
                ),
            onColumnOrderChange: (updaterOrValue) =>
                persistLocalStorage(
                    updaterOrValue,
                    localColumnOrder ?? [],
                    setLocalCleanColumnOrder,
                    onColumnOrderChange,
                ),
            onColumnPinningChange: (updaterOrValue) =>
                persistLocalStorage(
                    updaterOrValue,
                    localColumnPinning ?? { left: [], right: [] },
                    setLocalCleanColumnPinning,
                    onColumnPinningChange,
                ),
            onSortingChange: (updaterOrValue) =>
                persistLocalStorage(
                    updaterOrValue,
                    localColumnSorting ?? [],
                    setLocalColumnSorting,
                    onSortingChange,
                ),
            mrtTheme: (theme) =>
                isPrimaryBackgroundColor && {
                    baseBackgroundColor: theme.palette.background.default,
                },
            ...rest,
        } as MRT_TableOptions<MRT_RowData>
    }, [
        localColumnOrder,
        localColumnPinning,
        localColumnSizing,
        localColumnSorting,
        localColumnVisibility,
        options,
        setLocalColumnOrder,
        setLocalColumnPinning,
        setLocalColumnSizing,
        setLocalColumnSorting,
        setLocalColumnVisibility,
    ])

    const table = useMaterialReactTable(mergedOptions)
    return table
}

const persistLocalStorage = <T extends object>(
    updaterOrValue: Updater<T>,
    state: T,
    setState: Dispatch<SetStateAction<T | undefined>>,
    callback?: (updater: Updater<T>) => void,
) => {
    const newState =
        updaterOrValue instanceof Function
            ? updaterOrValue(state)
            : updaterOrValue
    setState(newState)
    callback?.(updaterOrValue)
}

const defaultColumnPinning = {
    left: [
        'mrt-row-select',
        'mrt-row-numbers',
        'mrt-row-drag',
        'mrt-row-pin',
        'mrt-row-actions',
        'mrt-row-expand',
    ],
    right: [],
}

const getCleanColumnPinning = (columnPinning: MRT_ColumnPinningState | undefined) => {
    const cleanColumnPinning: MRT_ColumnPinningState = { left: [], right: [] }

    if (columnPinning) {
        for (const side of ['left', 'right'] as const) {
            for (const colId of columnPinning[side] ?? []) {
                if (!colId.startsWith('mrt-')) {
                    cleanColumnPinning[side]!.push(colId)
                }
            }
        }
    }

    return cleanColumnPinning
}

const mergeColumnPinning = (
    a: MRT_ColumnPinningState | undefined,
    b: MRT_ColumnPinningState | undefined,
) => {
    const merged: MRT_ColumnPinningState = { left: [], right: [] }

    for (const side of ['left', 'right'] as const) {
        const currentSide = a?.[side] ?? []
        const additionSide = b?.[side] ?? []
        const combined = [...currentSide, ...additionSide]
        const unique = Array.from(new Set(combined))
        merged[side] = unique
    }

    return merged
}

const getCleanColumnOrder = (columnOrder: MRT_ColumnOrderState | undefined) => {
    if (!columnOrder) return []
    return columnOrder.filter((colId) => !colId.startsWith('mrt-'))
}
